import { useEffect, useState, useCallback } from "react";
import api from "../lib/api";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const MOV_LABEL = {
  carico: { label: "Carico", cls: "bg-[#E6F4F1] text-[#2A9D8F] border-[#2A9D8F]/20" },
  scarico: { label: "Scarico", cls: "bg-[#FBEDEE] text-[#E63946] border-[#E63946]/20" },
  produzione: { label: "Produzione", cls: "bg-[#F3F4F6] text-[#1D3557] border-[#1D3557]/20" },
  inventario: { label: "Inventario", cls: "bg-[#FEF5EB] text-[#F4A261] border-[#F4A261]/20" },
};

export default function Movimenti() {
  const [items, setItems] = useState([]);
  const [type, setType] = useState("all");

  const load = useCallback(async () => {
    const params = type !== "all" ? { type } : {};
    const { data } = await api.get("/movements", { params });
    setItems(data);
  }, [type]);

  useEffect(() => {
    load();
  }, [load]);

  const fmtDate = (iso) => {
    try {
      return new Date(iso).toLocaleString("it-IT", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return iso;
    }
  };

  return (
    <div className="p-8 lg:p-12 fade-up" data-testid="movimenti-page">
      <div className="mb-8">
        <div className="text-[10px] uppercase tracking-[0.2em] text-[#4B5563] mb-2">
          Registro Movimenti
        </div>
        <h1 className="font-display text-4xl lg:text-5xl font-bold tracking-tight text-[#0A0A0A]">
          Movimenti
        </h1>
      </div>

      <div className="mb-5 flex items-center gap-3">
        <label className="text-[10px] uppercase tracking-wider font-semibold text-[#4B5563]">
          Filtro tipo
        </label>
        <div className="w-56">
          <Select value={type} onValueChange={setType}>
            <SelectTrigger data-testid="movimenti-filter-select" className="rounded-none">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti</SelectItem>
              <SelectItem value="carico">Carico</SelectItem>
              <SelectItem value="scarico">Scarico</SelectItem>
              <SelectItem value="produzione">Produzione</SelectItem>
              <SelectItem value="inventario">Inventario</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="ml-auto font-mono-data text-xs text-[#4B5563]">
          {items.length} registrazioni
        </div>
      </div>

      <div className="bg-white border border-[#E5E7EB] overflow-x-auto" data-testid="movimenti-table">
        <table className="w-full">
          <thead className="bg-[#F9FAFB] border-b border-[#E5E7EB]">
            <tr className="text-left text-[10px] uppercase tracking-wider text-[#4B5563]">
              <th className="px-6 py-3 font-semibold">Data</th>
              <th className="px-6 py-3 font-semibold">Tipo</th>
              <th className="px-6 py-3 font-semibold">Prodotto</th>
              <th className="px-6 py-3 font-semibold">EAN-13</th>
              <th className="px-6 py-3 font-semibold text-right">Quantità</th>
              <th className="px-6 py-3 font-semibold text-right">Stock</th>
              <th className="px-6 py-3 font-semibold">Operatore</th>
              <th className="px-6 py-3 font-semibold">Nota</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-6 py-12 text-center text-sm text-[#4B5563]">
                  Nessun movimento registrato.
                </td>
              </tr>
            ) : (
              items.map((m) => {
                const meta = MOV_LABEL[m.type] || MOV_LABEL.carico;
                return (
                  <tr
                    key={m.id}
                    className="border-b border-[#E5E7EB] hover:bg-[#F9FAFB]"
                    data-testid={`movimento-row-${m.id}`}
                  >
                    <td className="px-6 py-3 font-mono-data text-xs text-[#4B5563]">
                      {fmtDate(m.timestamp)}
                    </td>
                    <td className="px-6 py-3">
                      <span className={`inline-block px-2 py-1 text-[10px] font-semibold uppercase tracking-wider border ${meta.cls}`}>
                        {meta.label}
                      </span>
                    </td>
                    <td className="px-6 py-3 text-sm text-[#0A0A0A]">{m.product_name}</td>
                    <td className="px-6 py-3 font-mono-data text-xs text-[#4B5563]">
                      {m.product_barcode}
                    </td>
                    <td className="px-6 py-3 text-right font-mono-data text-sm text-[#0A0A0A] font-semibold">
                      {m.quantity > 0 ? "+" : ""}
                      {m.quantity} {m.unit}
                    </td>
                    <td className="px-6 py-3 text-right font-mono-data text-sm text-[#4B5563]">
                      {m.stock_after} {m.unit}
                    </td>
                    <td className="px-6 py-3 text-xs text-[#4B5563]">{m.user_email || "—"}</td>
                    <td className="px-6 py-3 text-xs text-[#4B5563]">{m.note || "—"}</td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
